import {
  Checkbox,
  CheckboxGroup,
  RadioButton,
  Tooltip,
  Link,
  ContextualAlert,
} from '@paypalcorp/pp-react'
import React, {
  ChangeEvent,
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Accordion } from '../../../components/integration-builder/accordion'
import { CheckoutSimulatorContext } from '../../../components/integration-builder/context/CheckoutSimulatorContext'
import { InfoCard } from '../../../components/integration-builder/info-card'
import withAnalytics from '../../../utils/withAnalytics'
import styles from '../integration-builder.module.css'
import { ExternalLink } from '../../../components/icons'
import { removeGAParam } from '../../../components/pp-react-wrappers'

const Configure: FunctionComponent = () => {
  const fptiData = typeof window !== `undefined` && window.fpti
  const [show3ds, setShow3ds] = useState(false)
  const [checked3ds, setChecked3ds] = useState(true)
  const { integrationType, setParameter, parameters, options, updateSnippets } =
    useContext(CheckoutSimulatorContext)

  const [buttonsError, setButtonsError] = useState('')

  useEffect(() => {
    setShow3ds(
      options.find((option) => option.name === 'frontend')?.value === 'html' &&
        options.find((option) => option.name === 'backend')?.value === 'nodejs',
    )
  }, [options])

  useEffect(() => {
    updateSnippets()
  }, [parameters])

  const recordAnalytics = (optionName: string, category: string) => {
    if (fptiData && window.PAYPAL?.analytics?.instance) {
      let event = ''
      switch (category) {
        case 'payment-button':
          event = `${category}:${optionName}:${integrationType}`
          break
        case 'payment-freq':
          event = `${category}:${optionName}:${integrationType}`
          break
        case 'btn-layout-shape':
          event = `${category}:${optionName}_${parameters.buttonStyle}:${integrationType}`
          break
        case 'btn-style':
          event = `${category}:${parameters.buttonLayout}_${optionName}:${integrationType}`
          break
        default:
          break
      }
      window.PAYPAL.analytics.instance.record({
        data: {
          component: 'devdiscoverynodeweb',
          space_key: 'SKEXJE',
          e: 'cl',
          link: event,
          event_action: event,
          ...fptiData,
          page_name: fptiData.page.replace(/:/g, '_'),
          event_name: `${fptiData.page.replace(/:/g, '_')}_${event}`,
          pu: removeGAParam(window.location.href),
        },
      })
    }
  }

  const handleButtonsCheckboxChange = (
    event: FormEvent<HTMLFieldSetElement>,
  ) => {
    const target = event.target as HTMLInputElement
    let buttons = parameters.buttons || []
    if (
      target.name === 'payment-buttons_card' &&
      parameters.buttonLayout === 'horizontal'
    ) {
      setButtonsError(
        'Debit or credit card button can only be selected if layout is vertical',
      )
      return
    }

    if (integrationType !== 'advanced')
      buttons = buttons.filter(
        (button: string) => button !== 'payment-buttons_3ds',
      )

    if (integrationType !== 'standard')
      buttons = buttons.filter(
        (button: string) => button !== 'payment-buttons_card',
      )

    buttons = buttons.filter((button: string) => button !== target.name)

    if (
      parameters.buttonLayout === 'horizontal' &&
      buttons.filter((button: string) => button !== 'payment-buttons_3ds')
        .length >= 2
    ) {
      setButtonsError(
        'Only one additional button is allowed in horizontal layout',
      )
      target.checked = false
    } else {
      setButtonsError('')
    }

    if (target.checked) {
      buttons.push(target.name)
    }

    setParameter('buttons', buttons)

    if (target.name === 'payment-buttons_3ds') {
      setChecked3ds(target.checked)
    }
    const selectedButtons = buttons.map((button: string) => {
      return button.replace('payment-buttons_', '')
    })
    recordAnalytics(selectedButtons.toString(), 'payment-button')
  }

  const handlePaymentModelChange = (event: ChangeEvent<HTMLInputElement>) => {
    setParameter('paymentModel', event.target.value)

    if (event.target.value === 'recurring') {
      setParameter(
        'buttons',
        parameters.buttons.filter(
          (button: string) => button !== 'payment-buttons_venmo',
        ),
      )
    }

    recordAnalytics(event.target.value, 'payment-freq')
  }
  const handleButtonLayoutChange = (event: ChangeEvent<HTMLInputElement>) => {
    setParameter('buttonLayout', event.target.value)
    if (event.target.value === 'horizontal') {
      setParameter('buttons', [
        'payment-buttons_paypal',
        'payment-buttons_venmo',
      ])
    } else {
      setButtonsError('')
      setParameter('buttons', [
        'payment-buttons_paypal',
        'payment-buttons_paylater',
        'payment-buttons_venmo',
        'payment-buttons_card',
      ])
    }
    recordAnalytics(event.target.value, 'btn-layout-shape')
  }
  const handleButtonStyleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setParameter('buttonStyle', event.target.value)
    recordAnalytics(event.target.value, 'btn-style')
  }

  return (
    <div className={styles.checkout_simulator__accordion_container}>
      <Accordion
        number={1}
        color="secondary"
        heading="Select checkout options"
        content={
          <>
            <div className={styles.checkout_simulator__subtitle_wrapper}>
              <p className={styles.checkout_simulator__fragment_subtitle}>
                Select the payment methods
              </p>
              <Tooltip
                name="tooltip1"
                description="These payment options are available to your payers at checkout."
                windowId="window1"
                imagePosition="bottom-left"
              />
            </div>
            <p>
              These relevant payment methods will show up next to your PayPal
              button.
            </p>
            <div className={styles.paypal_payment}>
              <span>PayPal</span>
              <span>Payment using a PayPal account.</span>
            </div>
            <CheckboxGroup onChange={handleButtonsCheckboxChange}>
              {buttonsError ? (
                <ContextualAlert type="error">{buttonsError}</ContextualAlert>
              ) : (
                <></>
              )}
              <Checkbox
                // helperText="Payment using a Venmo account"
                label="Venmo"
                name="payment-buttons_venmo"
                defaultChecked={true}
                checked={
                  parameters?.paymentModel === 'recurring' &&
                  integrationType === 'standard'
                    ? false
                    : parameters?.buttons?.includes('payment-buttons_venmo')
                }
              />
              <Checkbox
                helperText="Payment using PayPal Pay Later installments"
                label="Pay Later"
                name="payment-buttons_paylater"
                defaultChecked={true}
                checked={parameters?.buttons?.includes(
                  'payment-buttons_paylater',
                )}
              />
              {integrationType === 'advanced' && show3ds ? (
                <Checkbox
                  helperText="Authenticated payment using a credit or debit card"
                  label="3DS"
                  name="payment-buttons_3ds"
                  checked={checked3ds && show3ds}
                />
              ) : (
                <></>
              )}

              {integrationType === 'advanced' && !show3ds ? (
                <InfoCard
                  content={
                    <p>
                      At this time, 3DS can only be configured for select
                      languages.{' '}
                      <Link
                        target="_blank"
                        href="/docs/checkout/advanced/customize/3d-secure/sdk/"
                        className={styles.integration_builder__external_link}
                      >
                        See our 3DS documentation
                      </Link>
                      <ExternalLink />
                      for help adding 3DS to your integration.
                    </p>
                  }
                />
              ) : (
                <></>
              )}

              {integrationType !== 'advanced' ? (
                <Checkbox
                  helperText="PayPal-managed guest checkout using a Credit or Debit card"
                  label="Debit or Credit Card"
                  name="payment-buttons_card"
                  defaultChecked={true}
                  checked={
                    parameters?.buttonLayout === 'vertical'
                      ? parameters?.buttons?.includes('payment-buttons_card')
                      : false
                  }
                />
              ) : (
                <></>
              )}
            </CheckboxGroup>
            {integrationType === 'advanced' ? (
              <>
                <InfoCard
                  content={
                    <p>
                      A{' '}
                      <Link
                        target="_blank"
                        href="/dashboard/applications/sandbox"
                        className={styles.integration_builder__external_link}
                      >
                        client token
                      </Link>
                      <ExternalLink />
                      is required to uniquely identify your buyer. This is
                      required to use card fields. You can find a complete
                      example in the{' '}
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href="https://github.com/paypal-examples/docs-examples/tree/main/advanced-integration"
                        className={styles.integration_builder__external_link}
                      >
                        client token
                      </a>
                      <ExternalLink />.
                    </p>
                  }
                />
                <p className={styles.checkout_simulator__fragment_subtitle}>
                  Button and field customization options
                </p>
                <p>
                  You can modify the checkout user interface to match your style
                  needs. See the{' '}
                  <Link
                    target="_blank"
                    href="/docs/checkout/advanced/customize/card-field-style/"
                    className={styles.integration_builder__external_link}
                  >
                    Card Fields Style Guide
                  </Link>
                  <ExternalLink /> for a full list of attributes.
                </p>
              </>
            ) : null}
            {integrationType === 'standard' ? (
              <>
                <div className={styles.checkout_simulator__subtitle_wrapper}>
                  <p className={styles.checkout_simulator__fragment_subtitle}>
                    Choose a payment model
                  </p>
                  <Tooltip
                    name="tooltip1"
                    description="Whether the purchase is a one-time or recurring payment."
                    windowId="window1"
                    imagePosition="bottom-left"
                  />
                </div>
                <p>
                  Process one-time payments using the JavaScript SDK or payments
                  for fixed amounts at regular intervals using the Catalog
                  Product API, Subscriptions API, and the{' '}
                  <Link
                    target="_blank"
                    href="/sdk/js/reference/"
                    className={styles.integration_builder__external_link}
                  >
                    JavaScript SDK
                  </Link>
                  <ExternalLink />
                </p>
                <RadioButton
                  id="radioButtonPayment"
                  name="payment"
                  className={styles.checkout_simulator__radio_button}
                  onChange={handlePaymentModelChange}
                  value={parameters.paymentModel}
                  options={[
                    {
                      label: 'One-time payment',
                      value: 'onetime-payment',
                      helperText: 'A single payment',
                    },
                    {
                      label: 'Subscription payment',
                      value: 'recurring',
                      helperText:
                        'A recurring payment for a fixed amount at regular intervals',
                    },
                  ]}
                />
                <p className={styles.checkout_simulator__fragment_subtitle}>
                  Choose your button layout
                </p>
                <p>Choose between vertical or horizontal button layouts</p>
                <RadioButton
                  id="radioButtonLayout"
                  name="layout"
                  className={styles.checkout_simulator__radio_button}
                  onChange={handleButtonLayoutChange}
                  value={parameters.buttonLayout}
                  options={[
                    {
                      label: 'Vertical layout',
                      value: 'vertical',
                      helperText: 'Buttons are stacked vertically',
                    },
                    {
                      label: 'Horizontal layout',
                      value: 'horizontal',
                      helperText: 'Buttons are shown side-by-side',
                    },
                  ]}
                />
              </>
            ) : (
              <></>
            )}
            <p className={styles.checkout_simulator__fragment_subtitle}>
              Choose your button style
            </p>
            <p>Choose between rectangular or pill-shaped buttons</p>
            <RadioButton
              id="radioButtonStyles"
              name="buttonStyles"
              className={styles.checkout_simulator__radio_button}
              onChange={handleButtonStyleChange}
              value={parameters?.buttonStyle}
              options={[
                {
                  label: 'Rectangle',
                  value: 'rect',
                  helperText:
                    'Buttons have four sides with slightly rounded corners',
                },
                {
                  label: 'Pill',
                  value: 'pill',
                  helperText:
                    'Buttons are shaped like a pill with semi-circle ends',
                },
              ]}
            />
          </>
        }
      />
    </div>
  )
}

export default withAnalytics(Configure)
